/**
 * The collector conversation — the concierge agent's interview mode.
 *
 * WHAT THIS IS
 * ------------
 * The homepage invites a collector to "share your thoughts": six questions
 * Matthew wrote (2026-09-05), in his order, asked by the concierge agent one
 * at a time, then an email opt-in inside the chat, then a 20%-off Amazon claim
 * code delivered by EMAIL — never revealed on screen. The click-through from
 * that email is the proof of engagement Gmail's sender reputation needs, which
 * is why the reward is deliberately not shown at the end of the conversation.
 *
 * Everything here is a pure function over plain data so it is reachable from
 * Jest (same reason `conciergeAgent.ts` splits its rules out). The database and
 * Anthropic edges live in `convex/conciergeAgent.ts` / `conciergeAgentRespond.ts`.
 *
 * WHAT THE MODEL MAY AND MAY NOT DECIDE
 * -------------------------------------
 * The model asks and listens. It does NOT decide consent: `request_consent`
 * only puts the consent sentence and an "I agree" button in front of the
 * collector, and consent is recorded server-side when THEY tap it. It does not
 * decide the reward either: `complete_interview` runs a mutation that refuses
 * unless consent is recorded and enough answers exist, and the code is drawn
 * and emailed by that mutation, not by anything the model writes.
 */

import type { FactKey } from './conciergeAgent';

export const INTERVIEW_MODE = 'interview' as const;

/** How many of the six must be answered before the reward is issued. */
export const MIN_ANSWERS_FOR_REWARD = 3;

/**
 * A printed insert card promised its discount unconditionally, so a scanner
 * who arrives through one is never refused it: the email step opens after
 * the first answer. RECOMMENDATION pending Matthew's sign-off (2026-09-06);
 * set to MIN_ANSWERS_FOR_REWARD to make card scanners earn it like everyone.
 */
export const INSERT_MIN_ANSWERS_FOR_REWARD = 1;

/** The 25% and 50% insert promotions are one unit per code. */
export const INSERT_REWARD_PRODUCTS_CAP = 1;

/** The reward, as the Seller Central promotion defines it. */
export const INTERVIEW_REWARD = {
  percentOff: 20,
  /** Every claim code batch made 2026-09-05 is "up to 3 products" per customer. */
  productsCap: 3,
  /** Which pool batch the codes come from; loaded by scripts/load-code-batch.mjs. */
  surface: 'amazon',
} as const;

/**
 * Matthew's six questions, verbatim, in his order. The `key` is the profile
 * fact the answer is stored under (see FACT_KEYS in conciergeAgent.ts).
 */
export const INTERVIEW_QUESTIONS = [
  { key: 'collectingStage', ask: 'Where would you say you are right now in your collecting journey?' },
  { key: 'idealBrand', ask: 'What would the ideal TCG brand do for you?' },
  { key: 'problemsSolved', ask: 'What problems would it solve for you?' },
  { key: 'challenges', ask: 'What challenges would it help you overcome?' },
  {
    key: 'belonging',
    ask: 'What sense of belonging and community could there be associated with it?',
  },
  {
    key: 'payMoreFor',
    ask: 'What would you gladly pay more money for if we could reach for the stars and go above and beyond?',
  },
] as const satisfies readonly { key: FactKey; ask: string }[];

export type InterviewKey = (typeof INTERVIEW_QUESTIONS)[number]['key'];

export const INTERVIEW_KEYS: readonly InterviewKey[] = INTERVIEW_QUESTIONS.map(
  (question) => question.key,
);

export function isInterviewKey(key: string): key is InterviewKey {
  return (INTERVIEW_KEYS as readonly string[]).includes(key);
}

/**
 * Customer-facing lines. Matthew's words where marked; the rest is drafted
 * for his approval and lives here so every surface says the same thing.
 */
export const INTERVIEW_COPY = {
  /** Matthew, verbatim. */
  headline: 'We are building a brand for collectors by collectors!',
  /** Matthew, verbatim (the "limited time" clause replaced by the real limit). */
  rewardLine: 'Let us know your thoughts and you will receive a special reward in your inbox!',
  /** Matthew, verbatim. */
  timeLine: 'This conversation should take between 5 and 15 minutes+.',
  /** The real limit: the stop rule is 20–50 solid records, so the first 50 collectors. */
  limitLine: 'Open to the first 50 collectors.',
  button: 'Share Your Thoughts',
  /**
   * The opening message, written by the server for free (no model call) the
   * moment the conversation starts. It carries the promise and the first
   * question so nobody waits on a round trip to find out what this is.
   */
  opening:
    'We are building a brand for collectors by collectors! Let us know your thoughts and you will receive a special reward in your inbox. This conversation should take between 5 and 15 minutes+.\n\nFirst question. Where would you say you are right now in your collecting journey?',
  /**
   * The opening for someone who scanned a printed insert card. `{tier}` is the
   * card's percent. DRAFT for Matthew's words; the first question is his.
   */
  insertOpening:
    'Thank you for scanning your card! Your {tier}% code is real and it is yours — tell us a little about your collecting and it lands in your inbox.\n\nFirst question. Where would you say you are right now in your collecting journey?',
  /**
   * Said when a card is scanned in the middle of a conversation that was
   * already running: the reward switches to the card's tier. DRAFT.
   */
  insertCardNote:
    'Got your {tier}% card — that code is yours. Keep going and it lands in your inbox.',
  /** Shown after `complete_interview` succeeds. No code here, on purpose. */
  closing: 'Thank you so much for your inputs!!\n\nWe super appreciate it.\n\nCheck your email for a special link!',
  /**
   * The consent text rendered beside the "I agree" button, two lines. Signed
   * off by Matthew 2026-09-06. Recorded verbatim (line break included) with
   * every consent so we can show exactly what was agreed.
   */
  consentText:
    'Yes — email me my reward and, now and then, InfinityVault news for collectors.\nI know I can unsubscribe at any time.',
  consentButton: 'I agree',
  /**
   * The two sentences the agent says, word for word, after asking where the
   * reward should go. Matthew signed off these exact lines (2026-09-06); a
   * paraphrase would not be what he approved.
   */
  emailAsk: 'The code will arrive by email. Tap "I agree" to opt in so we can send it to you.',
} as const;

// ========================================
// PROGRESS
// ========================================

export interface InterviewState {
  /** Interview keys already recorded on the profile, any order. */
  answered: readonly string[];
  /** Address the model asked consent for; consent itself is a server stamp. */
  email?: string;
  consentGivenAt?: number;
  completedAt?: number;
  /** Set when the thread was opened from a printed insert card. */
  campaignKey?: string;
  /** The card's promised percent; absent for the homepage conversation. */
  percentOff?: number;
}

/** What this conversation pays out: the card's tier, or the homepage 20%. */
export function interviewRewardFor(
  state: Pick<InterviewState, 'campaignKey' | 'percentOff'>,
): { percentOff: number; productsCap: number; surface: 'amazon' } {
  if (state.campaignKey && state.percentOff) {
    return { percentOff: state.percentOff, productsCap: INSERT_REWARD_PRODUCTS_CAP, surface: 'amazon' };
  }
  return { ...INTERVIEW_REWARD };
}

export function minAnswersFor(state: Pick<InterviewState, 'campaignKey'>): number {
  return state.campaignKey ? INSERT_MIN_ANSWERS_FOR_REWARD : MIN_ANSWERS_FOR_REWARD;
}

export function insertCardNote(percentOff: number): string {
  return INTERVIEW_COPY.insertCardNote.replace(/\{tier\}/g, String(percentOff));
}

export function insertOpening(percentOff: number): string {
  return INTERVIEW_COPY.insertOpening.replace(/\{tier\}/g, String(percentOff));
}

export interface InterviewProgress {
  answeredKeys: InterviewKey[];
  /** The next unanswered question in Matthew's order, or null when all six are in. */
  next: (typeof INTERVIEW_QUESTIONS)[number] | null;
  /** Enough answers for the reward — the model may move to the email. */
  enoughForReward: boolean;
  /** Consent tapped; the model may call `complete_interview`. */
  canComplete: boolean;
  completed: boolean;
}

export function interviewProgress(state: InterviewState): InterviewProgress {
  const answeredKeys = INTERVIEW_KEYS.filter((key) => state.answered.includes(key));
  const next = INTERVIEW_QUESTIONS.find((question) => !answeredKeys.includes(question.key)) ?? null;
  const enoughForReward = answeredKeys.length >= minAnswersFor(state);
  const completed = state.completedAt !== undefined;
  return {
    answeredKeys,
    next,
    enoughForReward,
    canComplete: !completed && enoughForReward && state.consentGivenAt !== undefined,
    completed,
  };
}

/** Loose on purpose: the mail provider is the real validator. Blocks only nonsense. */
export function looksLikeEmail(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed);
}

export function normaliseEmail(value: string): string {
  return value.trim().toLowerCase();
}

// ========================================
// THE PROMPT SECTION
// ========================================

/**
 * What the model is told in interview mode. Replaces the onboarding ladder
 * (gaps / depth / account invite) wholesale — this is a different job with a
 * fixed order, and mixing the two produced a model that wandered off to ask
 * about binder sizes mid-interview.
 */
export function interviewSection(state: InterviewState): string {
  const progress = interviewProgress(state);
  const reward = interviewRewardFor(state);
  const minAnswers = minAnswersFor(state);
  const rewardLine =
    reward.productsCap === 1
      ? `a ${reward.percentOff}% Amazon code, one use, one product`
      : `a ${reward.percentOff}% Amazon code for up to ${reward.productsCap} products`;
  const header = state.campaignKey
    ? `## The collector conversation — this is the job right now\n\nThey scanned a printed insert card promising ${reward.percentOff}% off. That code is THEIRS regardless of how much they say: after ${minAnswers} answer${minAnswers === 1 ? '' : 's'} you may move to the email whenever they want to, and if they only want the code, take the one answer and go straight there without pressure.`
    : '## The collector conversation — this is the job right now';

  if (progress.completed) {
    return `${header}

The conversation is FINISHED and the reward email has been sent. Do not ask any more of the six questions and do not offer anything else. If they say more, thank them briefly and let them go; if they ask about the reward, tell them to open the email and tap the link — never read a code out, you do not have one.`;
  }

  const done =
    progress.answeredKeys.length === 0
      ? 'They have not answered anything yet.'
      : `Answered so far: ${progress.answeredKeys
          .map((key) => INTERVIEW_QUESTIONS.find((q) => q.key === key)?.ask ?? key)
          .map((ask) => `"${ask}"`)
          .join(', ')}.`;

  const questions = INTERVIEW_QUESTIONS.map(
    (question, index) =>
      `${index + 1}. ${question.ask} → record with \`record_answer\` key \`${question.key}\``,
  ).join('\n');

  const nextStep = progress.next
    ? `Next question to ask, when it is time: "${progress.next.ask}".`
    : 'All six are answered.';

  const consentStep = state.consentGivenAt
    ? 'They have agreed to the email. Call `complete_interview` now, then say the closing line and stop.'
    : state.email
      ? `You have asked for consent at ${state.email}; the "I agree" button is on their screen. Wait for it. Do not ask again unless they say the address is wrong — then call \`request_consent\` with the corrected one.`
      : progress.enoughForReward
        ? 'They have given enough for the reward. When the questions are done — or sooner if they are short on time and say so — ask for the email address the reward should go to, then call `request_consent` with it. Right after the ask, say these two sentences exactly as written, unchanged: "' + INTERVIEW_COPY.emailAsk + '"'
        : `The reward needs at least ${minAnswers} real answer${minAnswers === 1 ? '' : 's'}. Keep asking before you move to the email.`;

  return `${header}

Matthew's six questions, in this order, ONE at a time. Ask, listen, and the moment they answer call \`record_answer\` with what they said in their own words. Then decide: if the answer is rich, ask ONE short follow-up that digs into it ("do it well" is the brief — fifteen minutes is welcome); if it is brief or they are in a hurry, take it and move to the next question. Never stack questions, never bullet the list at them, never grade their answers.

${questions}

${done}
${nextStep}
${consentStep}

Rules for this mode:
- Do NOT go into the onboarding questions (binder sizes, what they collect) unless it comes up on its own; this conversation is the whole point of the visit.
- Never reveal, invent or describe a discount code. The reward is ${rewardLine} and it arrives by EMAIL after "I agree". You may say that much.
- If they want to stop early with fewer than ${minAnswers} answers, thank them, tell them the reward needs a little more, and leave the door open — no pressure.
- Consent is theirs to give with the button. Never write "I agree" for them, never assume it.`;
}
